import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Heart, Lock, Eye, EyeOff, Gift, MessageCircle, ArrowRight } from 'lucide-react';
import { useQuestionnaire } from './QuestionnaireContext';
import { questionnaireData } from './questions';
import { Question, QuestionType } from './PersonalizationTypes';
export const PersonalizationQuestionnaire: React.FC = () => {
  const {
    state,
    showQuestionnaire,
    closeQuestionnaire,
    nextStep,
    prevStep,
    goToStep,
    updatePreference,
    completeQuestionnaire,
    skipQuestionnaire
  } = useQuestionnaire();
  const [showSensitiveContent, setShowSensitiveContent] = useState(false);
  const [animateDirection, setAnimateDirection] = useState<'next' | 'prev' | null>(null);
  const [questionnaireType, setQuestionnaireType] = useState('full');
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  // These are the indices of the 7 most important questions for monetization
  // Assuming these are the most relevant for monetization based on typical patterns
  const compactQuestionIndices = [0, 2, 5, 8, 11, 14, 17];
  // Get the appropriate questions based on questionnaire type
  const relevantQuestions = questionnaireType === 'full' ? questionnaireData : compactQuestionIndices.map(index => questionnaireData[index]);
  const currentQuestion = relevantQuestions[state.currentStep - 1];
  const totalSteps = questionnaireType === 'full' ? 20 : 7;
  const handleNext = () => {
    setAnimateDirection('next');
    if (state.currentStep === totalSteps) {
      setShowCompletionScreen(true);
    } else {
      nextStep();
    }
  };
  const handlePrev = () => {
    setAnimateDirection('prev');
    prevStep();
  };
  const handleSkip = () => {
    skipQuestionnaire();
  };
  const handleComplete = () => {
    completeQuestionnaire();
  };
  const handleSingleSelect = (value: string) => {
    updatePreference(currentQuestion.id as any, value);
  };
  const handleMultiSelect = (value: string) => {
    const currentValues = state.preferences[currentQuestion.id as keyof typeof state.preferences] as string[] || [];
    if (currentValues.includes(value)) {
      updatePreference(currentQuestion.id as any, currentValues.filter(v => v !== value));
    } else {
      updatePreference(currentQuestion.id as any, [...currentValues, value]);
    }
  };
  const handleSlider = (value: number) => {
    updatePreference(currentQuestion.id as any, value);
  };
  const handleToggle = (value: boolean) => {
    updatePreference(currentQuestion.id as any, value);
  };
  const renderQuestionContent = (question: Question) => {
    const currentValue = state.preferences[question.id as keyof typeof state.preferences];
    if (question.sensitiveContent && !showSensitiveContent) {
      return <div className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-15165">
          <Lock className="w-12 h-12 text-pink-500 mb-4" data-id="element-15166" />
          <h3 className="text-lg font-medium mb-2" data-id="element-15167">Sensitive Content</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center" data-id="element-15168">
            This question contains adult content preferences. You can skip this
            or reveal the content.
          </p>
          <button onClick={() => setShowSensitiveContent(true)} className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg transition-colors" data-id="element-15169">
            <div className="flex items-center gap-2" data-id="element-15170">
              <Eye size={16} data-id="element-15171" />
              <span data-id="element-15172">Reveal Content</span>
            </div>
          </button>
        </div>;
    }
    switch (question.type) {
      case 'single-select':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-15173">
            {question.options?.map(option => <button key={option.value} onClick={() => handleSingleSelect(option.value)} className={`flex items-center p-4 rounded-lg border transition-all ${currentValue === option.value ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700'}`} data-id="element-15174">
                <div className="flex-1 flex items-center gap-3" data-id="element-15175">
                  {option.icon && <span className="text-xl" data-id="element-15176">{option.icon}</span>}
                  <div className="text-left" data-id="element-15177">
                    <div className="font-medium" data-id="element-15178">{option.label}</div>
                    {option.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-15179">
                        {option.description}
                      </div>}
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${currentValue === option.value ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-15180">
                  {currentValue === option.value && <Check size={12} data-id="element-15181" />}
                </div>
              </button>)}
          </div>;
      case 'multi-select':
        return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-15182">
            {question.options?.map(option => {
            const isSelected = Array.isArray(currentValue) && currentValue.includes(option.value);
            return <button key={option.value} onClick={() => handleMultiSelect(option.value)} className={`flex items-center p-3 rounded-lg border transition-all ${isSelected ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700'}`} data-id="element-15183">
                  <div className="flex-1 flex items-center gap-2" data-id="element-15184">
                    {option.icon && <span className="text-lg" data-id="element-15185">{option.icon}</span>}
                    <div className="text-left text-sm font-medium" data-id="element-15186">
                      {option.label}
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-15187">
                    {isSelected && <Check size={12} data-id="element-15188" />}
                  </div>
                </button>;
          })}
          </div>;
      case 'slider':
        return <div className="w-full px-4" data-id="element-15189">
            <div className="flex justify-between mb-2 text-sm" data-id="element-15190">
              <span data-id="element-15191">{question.min}</span>
              <span data-id="element-15192">{question.max}</span>
            </div>
            <input type="range" min={question.min || 1} max={question.max || 10} step={question.step || 1} value={currentValue as number || question.defaultValue || 5} onChange={e => handleSlider(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-500" data-id="element-15193" />
            <div className="mt-4 text-center" data-id="element-15194">
              <div className="text-2xl font-bold text-pink-500" data-id="element-15195">
                {currentValue || question.defaultValue || 5}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-15196">
                {currentValue === 1 ? 'Very Low' : currentValue === question.max ? 'Very High' : 'Moderate'}
              </div>
            </div>
          </div>;
      case 'toggle':
        return <div className="flex flex-col items-center justify-center" data-id="element-15197">
            <button onClick={() => handleToggle(!currentValue)} className={`w-16 h-8 rounded-full relative transition-colors ${currentValue ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-15198">
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${currentValue ? 'translate-x-8' : 'translate-x-1'}`} data-id="element-15199"></span>
            </button>
            <div className="mt-4 text-center text-sm font-medium" data-id="element-15200">
              {currentValue ? 'Yes' : 'No'}
            </div>
          </div>;
      default:
        return null;
    }
  };
  const renderCompletionScreen = () => {
    const tokenAmount = questionnaireType === 'full' ? 200 : 75;
    return <div className="flex flex-col items-center justify-center py-8 px-6 text-center animate-in fade-in zoom-in-95 duration-300" data-id="element-15201">
        <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-6" data-id="element-15202">
          <Heart className="text-white w-10 h-10" data-id="element-15203" />
        </div>
        <h2 className="text-2xl font-bold mb-2" data-id="element-15204">Congratulations!</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-15205">
          You've completed the {questionnaireType === 'full' ? 'full' : 'quick'}{' '}
          questionnaire
        </p>
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/10 rounded-lg p-4 mb-6 w-full max-w-md" data-id="element-15206">
          <div className="flex items-center justify-center gap-3" data-id="element-15207">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold" data-id="element-15208">
              💗
            </div>
            <div className="text-left" data-id="element-15209">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-15210">
                You've earned
              </p>
              <p className="text-xl font-bold text-pink-600 dark:text-pink-400" data-id="element-15211">
                {tokenAmount} LuvTokens
              </p>
            </div>
          </div>
        </div>
        <button onClick={handleComplete} className="w-full max-w-md py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium mb-4 hover:shadow-lg hover:shadow-pink-500/20 transition-all flex items-center justify-center gap-2" data-id="element-15212">
          <span data-id="element-15213">Continue to Chat</span>
          <ArrowRight size={18} data-id="element-15214" />
        </button>
        <a href="#" className="text-sm text-pink-500 hover:underline flex items-center gap-1" onClick={e => {
        e.preventDefault();
        handleComplete();
        // Here you would add navigation to rewards section
        const navigateEvent = new CustomEvent('navigate', {
          detail: {
            section: 'rewards'
          }
        });
        document.dispatchEvent(navigateEvent);
      }} data-id="element-15215">
          <Gift size={14} data-id="element-15216" />
          <span data-id="element-15217">View my rewards</span>
        </a>
      </div>;
  };
  if (!showQuestionnaire) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-15218">
      <div className="relative w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-300" data-id="element-15219">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800" data-id="element-15220">
          <div className="flex items-center justify-between mb-2" data-id="element-15221">
            <div className="flex items-center gap-2" data-id="element-15222">
              <Heart className="text-pink-500" size={20} data-id="element-15223" />
              <h2 className="text-lg font-semibold" data-id="element-15224">Get to Know You</h2>
            </div>
            <div className="flex items-center gap-2" data-id="element-15225">
              <button onClick={handleSkip} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-15226">
                Skip
              </button>
              <button onClick={closeQuestionnaire} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-15227">
                <X size={20} className="text-gray-500 dark:text-gray-400" data-id="element-15228" />
              </button>
            </div>
          </div>
          {!showCompletionScreen && <>
              {/* Questionnaire Type Selection */}
              <div className="flex gap-3 mb-3" data-id="element-15229">
                <button onClick={() => setQuestionnaireType('full')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${questionnaireType === 'full' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} data-id="element-15230">
                  <div className="flex flex-col items-center" data-id="element-15231">
                    <span className="text-xs mb-1" data-id="element-15232">Full Experience</span>
                    <div className="flex items-center gap-1" data-id="element-15233">
                      <span data-id="element-15234">💗</span>
                      <span className="font-bold" data-id="element-15235">200 LuvTokens</span>
                    </div>
                    <span className="text-xs mt-1" data-id="element-15236">20 Questions</span>
                  </div>
                </button>
                <button onClick={() => setQuestionnaireType('compact')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${questionnaireType === 'compact' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} data-id="element-15237">
                  <div className="flex flex-col items-center" data-id="element-15238">
                    <span className="text-xs mb-1" data-id="element-15239">Quick Version</span>
                    <div className="flex items-center gap-1" data-id="element-15240">
                      <span data-id="element-15241">💗</span>
                      <span className="font-bold" data-id="element-15242">75 LuvTokens</span>
                    </div>
                    <span className="text-xs mt-1" data-id="element-15243">7 Questions</span>
                  </div>
                </button>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/10 rounded-lg p-3 flex items-center justify-between" data-id="element-15244">
                <div className="flex items-center gap-2" data-id="element-15245">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold animate-pulse-slow" data-id="element-15246">
                    💗
                  </div>
                  <div data-id="element-15247">
                    <p className="text-sm font-medium text-pink-600 dark:text-pink-400" data-id="element-15248">
                      Complete & earn{' '}
                      <span className="font-bold" data-id="element-15249">
                        {questionnaireType === 'full' ? '200' : '75'} LuvTokens
                      </span>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-15250">
                      Personalize your experience for better matches &
                      conversations
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-xs font-medium text-pink-600 dark:text-pink-400 border border-pink-100 dark:border-pink-800/30" data-id="element-15251">
                  Exclusive Reward
                </div>
              </div>
            </>}
        </div>
        {!showCompletionScreen && <>
            {/* Progress Bar */}
            <div className="w-full h-1 bg-gray-100 dark:bg-gray-800" data-id="element-15252">
              <div className="h-full bg-pink-500 transition-all duration-300" style={{
            width: `${state.currentStep / totalSteps * 100}%`
          }} data-id="element-15253"></div>
            </div>
            {/* Content */}
            <div className="px-6 py-8" data-id="element-15254">
              <div className={`transition-all duration-300 ${animateDirection === 'next' ? 'animate-slide-left' : animateDirection === 'prev' ? 'animate-slide-right' : ''}`} data-id="element-15255">
                <h3 className="text-xl font-bold mb-2" data-id="element-15256">
                  {currentQuestion.question}
                </h3>
                {currentQuestion.description && <p className="text-gray-500 dark:text-gray-400 mb-6" data-id="element-15257">
                    {currentQuestion.description}
                  </p>}
                <div className="mt-6" data-id="element-15258">
                  {renderQuestionContent(currentQuestion)}
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-15259">
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-15260">
                <span data-id="element-15261">{state.currentStep}</span>
                <span data-id="element-15262">/</span>
                <span data-id="element-15263">{totalSteps}</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-15264">
                {state.currentStep > 1 && <button onClick={handlePrev} className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1" data-id="element-15265">
                    <ChevronLeft size={16} data-id="element-15266" />
                    <span data-id="element-15267">Back</span>
                  </button>}
                <button onClick={handleNext} className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg flex items-center gap-1" data-id="element-15268">
                  <span data-id="element-15269">
                    {state.currentStep === totalSteps ? 'Finish' : 'Next'}
                  </span>
                  {state.currentStep !== totalSteps && <ChevronRight size={16} data-id="element-15270" />}
                </button>
              </div>
            </div>
            {/* Step Indicators */}
            <div className="absolute bottom-20 left-0 right-0 flex justify-center gap-1 px-6 overflow-x-auto py-2" data-id="element-15271">
              {Array.from({
            length: totalSteps
          }).map((_, index) => <button key={index} onClick={() => goToStep(index + 1)} className={`w-2 h-2 rounded-full transition-all ${state.currentStep === index + 1 ? 'bg-pink-500 w-4' : 'bg-gray-300 dark:bg-gray-700'}`} data-id="element-15272"></button>)}
            </div>
          </>}
        {/* Completion Screen */}
        {showCompletionScreen && renderCompletionScreen()}
      </div>
      <style jsx data-id="element-15273">{`
        @keyframes slide-left {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slide-right {
          0% {
            opacity: 0;
            transform: translateX(-20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-left {
          animation: slide-left 0.3s ease-out forwards;
        }
        .animate-slide-right {
          animation: slide-right 0.3s ease-out forwards;
        }
      `}</style>
    </div>;
};